import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';

const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

/**
 * Suppliers (Inventory module) — vendors you buy from. Invoices/expenses can
 * link to a supplier → spend metrics per supplier. Opt-in like the rest of
 * the inventory module.
 */
const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const user = getAuth().currentUser;

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const [supSnap, expSnap] = await Promise.all([
        getDocs(collection(db, 'users', user.uid, 'suppliers')),
        getDocs(collection(db, 'users', user.uid, 'expenses')),
      ]);
      setSuppliers(supSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setExpenses(expSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Spend per supplier (linked invoices/expenses only)
  const spendBySupplier = useMemo(() => {
    const map = {};
    for (const e of expenses) {
      if (!e.supplierId) continue;
      map[e.supplierId] = (map[e.supplierId] || 0) + (e.amount || 0);
    }
    return map;
  }, [expenses]);

  const handleDelete = async (sup) => {
    const res = await Swal.fire({
      title: `Delete "${sup.name}"?`,
      text: 'Invoices linked to it keep their data (the link just detaches).',
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
    });
    if (!res.isConfirmed) return;
    const db = getFirestore();
    await deleteDoc(doc(db, 'users', user.uid, 'suppliers', sup.id));
    await load();
  };

  return (
    <AdminLayout title="Suppliers">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 font-weight-bold">Suppliers</h5>
          <small className="text-muted">Your vendors — invoices link to them for spend metrics</small>
        </div>
        <button className="btn btn-primary btn-sm" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={() => { setEditing(null); setShowForm(true); }}>
          <i className="fas fa-plus mr-1"></i> New supplier
        </button>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Supplier</th><th>Contact</th><th>Phone</th>
                <th className="text-end">Total spend (linked)</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: '#ff6b35' }} /></td></tr>
              ) : suppliers.length === 0 ? (
                <tr><td colSpan="5" className="text-center py-4 text-muted">No suppliers yet — create the first one (Restaurant Depot, Sysco…).</td></tr>
              ) : suppliers.map(sup => (
                <tr key={sup.id}>
                  <td>
                    <div className="fw-bold">{sup.name}</div>
                    {sup.notes && <small className="text-muted">{sup.notes}</small>}
                  </td>
                  <td>
                    {sup.preferred && <span className="badge badge-warning mr-1">Preferred</span>}
                    {sup.contactName || '—'}{sup.email ? <><br /><small className="text-muted">{sup.email}</small></> : null}
                    {sup.paymentTerms && sup.paymentTerms !== 'due_on_receipt' ? <><br /><small className="text-muted">{sup.paymentTerms.replace('net_', 'Net ')}{sup.leadTimeDays != null ? ` · ${sup.leadTimeDays}d lead` : ''}</small></> : sup.leadTimeDays != null ? <><br /><small className="text-muted">{sup.leadTimeDays}d lead</small></> : null}
                  </td>
                  <td>{sup.phone || '—'}</td>
                  <td className="text-end fw-bold">{spendBySupplier[sup.id] ? money(spendBySupplier[sup.id]) : '—'}</td>
                  <td className="text-end text-nowrap">
                    <button className="btn btn-sm btn-outline-secondary mr-1" onClick={() => { setEditing(sup); setShowForm(true); }}>
                      <i className="fas fa-pen"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(sup)}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <SupplierFormModal
          supplier={editing}
          onClose={() => setShowForm(false)}
          onSaved={async () => { setShowForm(false); await load(); }}
        />
      )}
    </AdminLayout>
  );
};

const SupplierFormModal = ({ supplier, onClose, onSaved }) => {
  const user = getAuth().currentUser;
  const [form, setForm] = useState({
    name: supplier?.name || '',
    legalName: supplier?.legalName || '',
    contactName: supplier?.contactName || '',
    phone: supplier?.phone || '',
    email: supplier?.email || '',
    website: supplier?.website || '',
    address: supplier?.address || '',
    taxId: supplier?.taxId || '',
    accountNumber: supplier?.accountNumber || '',
    paymentTerms: supplier?.paymentTerms || 'due_on_receipt',
    leadTimeDays: supplier?.leadTimeDays ?? '',
    categoryTags: supplier?.categoryTags || '',
    preferred: supplier?.preferred === true,
    notes: supplier?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { Swal.fire('Missing', 'Name is required', 'warning'); return; }
    setSaving(true);
    try {
      const db = getFirestore();
      const ref = supplier?.id
        ? doc(db, 'users', user.uid, 'suppliers', supplier.id)
        : doc(collection(db, 'users', user.uid, 'suppliers'));
      await setDoc(ref, {
        name: form.name.trim(),
        legalName: form.legalName.trim() || null,
        contactName: form.contactName.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        website: form.website.trim() || null,
        taxId: form.taxId.trim() || null,
        accountNumber: form.accountNumber.trim() || null,
        paymentTerms: form.paymentTerms,
        leadTimeDays: form.leadTimeDays === '' ? null : parseInt(form.leadTimeDays, 10),
        categoryTags: form.categoryTags.trim() || null,
        preferred: form.preferred,
        notes: form.notes.trim() || null,
        updatedAt: serverTimestamp(),
        ...(supplier?.id ? {} : { createdAt: serverTimestamp() }),
      }, { merge: true });
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">{supplier ? 'Edit' : 'New'} supplier</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-6 form-group">
                <label className="font-weight-bold">Name *</label>
                <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Restaurant Depot" />
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Legal name</label>
                <input className="form-control" value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} placeholder="Legal entity name" />
              </div>
            </div>
            <div className="row">
              <div className="col-6 form-group">
                <label className="font-weight-bold">Contact name</label>
                <input className="form-control" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Phone</label>
                <input className="form-control" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="row">
              <div className="col-6 form-group">
                <label className="font-weight-bold">Email</label>
                <input type="email" className="form-control" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Website</label>
                <input className="form-control" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" />
              </div>
            </div>
            <div className="form-group">
              <label className="font-weight-bold">Address</label>
              <input className="form-control" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>

            <div className="row" style={{ background: '#f9fafb', borderRadius: 12, padding: '10px 4px', margin: '0 0 12px' }}>
              <div className="col-12"><small className="text-muted font-weight-bold text-uppercase">Commercial terms (ERP)</small></div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Tax ID</label>
                <input className="form-control" value={form.taxId} onChange={(e) => setForm({ ...form, taxId: e.target.value })} />
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Account #</label>
                <input className="form-control" value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} placeholder="Your account # with them" />
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Payment terms</label>
                <select className="form-control" value={form.paymentTerms} onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })}>
                  <option value="due_on_receipt">Due on receipt</option>
                  <option value="net_7">Net 7</option>
                  <option value="net_15">Net 15</option>
                  <option value="net_30">Net 30</option>
                  <option value="net_60">Net 60</option>
                </select>
              </div>
              <div className="col-6 form-group">
                <label className="font-weight-bold">Lead time (days)</label>
                <input type="number" min="0" className="form-control" value={form.leadTimeDays} onChange={(e) => setForm({ ...form, leadTimeDays: e.target.value })} placeholder="order→delivery" />
              </div>
              <div className="col-12 form-group">
                <label className="font-weight-bold">What they supply (tags)</label>
                <input className="form-control" value={form.categoryTags} onChange={(e) => setForm({ ...form, categoryTags: e.target.value })} placeholder="e.g. produce, meat, packaging" />
              </div>
              <div className="col-12">
                <div className="custom-control custom-switch">
                  <input type="checkbox" className="custom-control-input" id="preferred" checked={form.preferred} onChange={(e) => setForm({ ...form, preferred: e.target.checked })} />
                  <label className="custom-control-label" htmlFor="preferred">Preferred supplier (shows first)</label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="font-weight-bold">Notes</label>
              <textarea className="form-control" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuppliersPage;
